import React, { useEffect, useState, useCallback, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  Card,
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  NativeSelect,
  Chip,
  MenuItem,
  CircularProgress,
  Grid,
  Paper,
  Skeleton,
  Fade,
  Tooltip,
  IconButton,
  Divider,
} from "@mui/material";
import Page from "src/components/Page";
import {
  createChargingSite,
  deleteChargingSiteById,
  getChargingSiteById,
  getChargingSiteList,
  updateChargingSiteById,
  getAvailableCharger,
  getTeamChargerIds,
} from "src/react-query/endPoints";
import axios from "../../util/axios";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useAuth } from "src/hooks/useAuth.hook";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { ReactComponent as ZoneLogo } from "../../image/zoneLogo.svg";
import { BaseTable } from "../BaseTable/BaseTable";
import { useAccess } from "src/hooks/useAccess.hook";
import enums, { FEATURE } from "src/util/enums";
import { chargingSiteColumn } from "./ChargingSiteList.constant";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import mapFill from "@iconify/icons-eva/map-fill";
import batteryFill from "@iconify/icons-eva/battery-fill";
import homeFill from "@iconify/icons-eva/home-fill";
import settingsFill from "@iconify/icons-eva/settings-fill";
import InputAdornment from "@mui/material/InputAdornment";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";

const {
  actions: { setAlert },
} = alertSlice;

const BoxStyle = styled("div")(({ theme }) => ({
  width: "100%",
  border: "1px solid #00000036",
  placeItems: "center",
  padding: "1rem",
  borderRadius: "10px",
}));

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const ChargingSite = () => {
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();

  // Check if a team (not company) is selected
  const isTeamSelected = teamId && teamId !== user?.company?.id;

  const [chargerList, setChargerList] = useState();
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [createZoneBtn, setCreateZoneBtn] = useState(false);
  const [updateChargingSite, setUpdateChargingSite] = useState({
    value: false,
    zoneId: "",
  });
  const isReadOnly = useAccess(FEATURE.CHARGINGSITE, "R");
  const hasEditAccess = useAccess(FEATURE.CHARGINGSITE, "U");
  const hasCreateAccess = useAccess(FEATURE.CHARGINGSITE, "C");
  const hasDeleteAccess = useAccess(FEATURE.CHARGINGSITE, "D");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    zoneName: "",
    isLoadBalancingReq: 0,
    loadBalancingUnit: "kW",
    loadBalancingValue: "",
    chargers: [],
  });

  // Cleanup effect to reset refs when component unmounts
  useEffect(() => {
    return () => {
      hasFetchedChargingSiteRef.current = false;
      initialDataFetchedRef.current = false;
      // Clear available chargers on unmount
      setAvailableChargers([]);
    };
  }, []);
  const [availableChargers, setAvailableChargers] = useState([]);
  const [selectedChargerId, setSelectedChargerId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const initialDataFetchedRef = useRef(false);
  const hasFetchedChargingSiteRef = useRef(false);

  // Check if we're in add/edit mode based on route
  const isAddMode = location.pathname.includes("/new");
  const isEditMode = id && !isAddMode;

  const errorNotification = (err) => {
    return dispatch(
      setAlert({
        open: true,
        message: err.message,
        severity: enums.SEVERITY.ERROR,
      })
    );
  };

  const handleAction = async ({ action, id }) => {
    if (!teamId) {
      console.error("teamId is not available");
      return;
    }

    if (action === "forDelete") {
      try {
        const response = await axios.delete(deleteChargingSiteById(teamId, id));
        if (response.data.success) {
          dispatch(
            setAlert({
              open: true,
              message: "Zone deleted successfully",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          getChargerList();
        }
      } catch (err) {
        errorNotification(err);
      }
    } else if (action === "view") {
      setUpdateChargingSite({ value: true, zoneId: id });
      navigate(`/charging-site/${id}`);
    } else {
      setUpdateChargingSite({ value: true, zoneId: id });
      navigate(`/charging-site/${id}`);
    }
  };

  const fetchChargingSiteById = async (zoneId) => {
    if (!teamId) {
      console.error("teamId is not available for fetchChargingSiteById");
      return;
    }

    try {
      const response = await axios.get(getChargingSiteById(teamId, zoneId));

      // Extract data with comprehensive field mapping
      const apiData = response.data.data || response.data || {};

      // Extract the charging site data from the nested structure
      const chargingSiteData = apiData.chargingSite || apiData;

      const updatedFormData = {
        zoneName:
          chargingSiteData.zoneName ||
          chargingSiteData.zone_name ||
          chargingSiteData.name ||
          "",
        isLoadBalancingReq: Number(
          chargingSiteData.isLoadBalancingReq ||
            chargingSiteData.is_load_balancing_req ||
            chargingSiteData.loadBalancingRequired ||
            chargingSiteData.load_balancing_required ||
            0
        ),
        loadBalancingUnit:
          chargingSiteData.loadBalancingUnit ||
          chargingSiteData.load_balancing_unit ||
          chargingSiteData.unit ||
          "kW",
        loadBalancingValue:
          chargingSiteData.loadBalancingValue ||
          chargingSiteData.load_balancing_value ||
          chargingSiteData.value ||
          chargingSiteData.maxLoad ||
          "",
        chargers:
          chargingSiteData.chargers ||
          chargingSiteData.attachedChargers ||
          chargingSiteData.chargerList ||
          [],
      };

      // Set the createZoneBtn to true for edit mode
      setCreateZoneBtn(true);

      // Reset form submission state when entering edit mode
      setIsFormSubmitting(false);

      // Set form data
      setFormData(updatedFormData);
    } catch (err) {
      console.error("Error fetching charging site by ID:", err);
      errorNotification(err);
    }
  };

  const chargingSiteColumns = chargingSiteColumn({
    createZoneBtn,
    setCreateZoneBtn,
    handleAction,
    isReadOnly,
  });

  // Calculate statistics
  const totalSites = chargerList?.length || 0;
  const sitesWithLoadBalancing =
    chargerList?.filter((site) => site.isLoadBalancingReq === 1).length || 0;

  // Search functionality
  const handleSearching = (event) => {
    const dataToSearchFrom = chargerList || [];
    const searchTerm = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchTerm);

    if (searchTerm) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "zone_name",
            "load_balancing_value",
            "load_balancing_unit",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              value ? value.toString().toLowerCase() : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchTerm)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else {
      setTableData([...(chargerList || [])]);
    }
  };

  const handleRefresh = () => {
    getChargerList();
  };

  // Row click handler for navigation to edit mode
  const rowOrCellClickRedirect = ({ id, row }) => {
    // if (isReadOnly) return;
    navigate(`/charging-site/${id}`);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prevData) => {
      const newData = {
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      };
      return newData;
    });
  };

  const handleSubmitForm = async (event) => {
    event.preventDefault();

    setIsFormSubmitting(true);

    // Validation
    const errors = [];

    // Validate zone name
    if (!formData.zoneName || formData.zoneName.trim() === "") {
      errors.push("Zone name is required");
    }

    // Validate load balancing unit
    const validUnits = ["kW", "kWh", "A"];
    if (!validUnits.includes(formData.loadBalancingUnit)) {
      errors.push("Load balancing unit must be kW, kWh, or A");
    }

    // Validate load balancing value
    if (
      formData.loadBalancingValue === "" ||
      formData.loadBalancingValue === null
    ) {
      errors.push("Load balancing value is required");
    } else if (
      isNaN(formData.loadBalancingValue) ||
      parseFloat(formData.loadBalancingValue) < 0
    ) {
      errors.push("Load balancing value must be a non-negative number");
    }

    // If there are validation errors, show them and return
    if (errors.length > 0) {
      dispatch(
        setAlert({
          open: true,
          message: errors.join(". "),
          severity: enums.SEVERITY.ERROR,
        })
      );
      return;
    }

    // Prepare data for submission - extract only charger IDs
    // Safely extract charger IDs, handling different data structures
    let chargerIds = [];

    if (formData.chargers && Array.isArray(formData.chargers)) {
      // Try different possible property names for charger ID
      chargerIds = formData.chargers
        .filter(
          (charger) =>
            charger &&
            (charger.chargepoint_id || charger.id || charger.chargerId)
        )
        .map(
          (charger) => charger.chargepoint_id || charger.id || charger.chargerId
        );
    }

    const submissionData = {
      ...formData,
      chargers: chargerIds,
    };

    if (updateChargingSite.value) {
      if (!teamId) {
        console.error("teamId is not available for update");
        setIsFormSubmitting(false);
        return;
      }

      try {
        const response = await axios.put(
          updateChargingSiteById(teamId, updateChargingSite.zoneId),
          submissionData
        );
        if (response.data.success) {
          dispatch(
            setAlert({
              open: true,
              message: "Zone updated successfully",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          getChargerList();
          setUpdateChargingSite({ value: false, zoneId: "" });
          setCreateZoneBtn(false);
          navigate("/charging-site");
        }
      } catch (err) {
        errorNotification(err);
      }
    } else {
      if (!teamId) {
        console.error("teamId is not available for create");
        setIsFormSubmitting(false);
        return;
      }

      try {
        const response = await axios.post(
          createChargingSite(teamId),
          submissionData
        );
        if (response.data.success) {
          dispatch(
            setAlert({
              open: true,
              message: "Zone created successfully",
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          getChargerList();
          setCreateZoneBtn(false);
          navigate("/charging-site");
        }
      } catch (err) {
        errorNotification(err);
      }
    }

    // Reset loading state after all operations complete
    setIsFormSubmitting(false);
  };

  const fetchAvailableChargers = async () => {
    if (!teamId) {
      console.error("teamId is not available for fetchAvailableChargers");
      return;
    }

    try {
      const response = await axios.get(getTeamChargerIds(teamId, true));

      // Extract charger IDs from the response - try different possible structures
      let chargerIds = [];

      if (
        response.data?.chargerIds &&
        Array.isArray(response.data.chargerIds)
      ) {
        chargerIds = response.data.chargerIds;
      } else if (
        response.data?.data?.chargerIds &&
        Array.isArray(response.data.data.chargerIds)
      ) {
        chargerIds = response.data.data.chargerIds;
      } else if (Array.isArray(response.data)) {
        chargerIds = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        chargerIds = response.data.data;
      }

      // Convert charger IDs to objects for the dropdown
      const allChargers = chargerIds.map((id) => ({
        chargepoint_id: id,
        name: id,
      }));

      setAvailableChargers(allChargers);
    } catch (err) {
      console.error("Error fetching available chargers:", err);
      setAvailableChargers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getChargerList = async () => {
    if (!teamId) {
      console.error("teamId is not available for getChargerList");
      return;
    }

    setIsLoading(true);
    try {
      if (isAddMode) {
        // In NEW mode: fetch available charger IDs for the team
        const response = await axios.get(getTeamChargerIds(teamId, true));

        // Convert charger IDs to objects for display
        let chargerIds = [];

        if (
          response.data?.chargerIds &&
          Array.isArray(response.data.chargerIds)
        ) {
          chargerIds = response.data.chargerIds;
        } else if (
          response.data?.data?.chargerIds &&
          Array.isArray(response.data.data.chargerIds)
        ) {
          chargerIds = response.data.data.chargerIds;
        } else if (Array.isArray(response.data)) {
          chargerIds = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          chargerIds = response.data.data;
        }

        const availableChargersList = chargerIds.map((id) => ({
          zone_id: id,
          zoneName: `Available Charger: ${id}`,
          loadBalancingUnit: "N/A",
          loadBalancingValue: "N/A",
          chargers: [],
        }));

        setChargerList(availableChargersList);
        setTableData(availableChargersList);
      } else {
        // In EDIT mode or list view: fetch existing charging sites
        const response = await axios.get(getChargingSiteList(teamId));

        // Handle different response structures
        let chargingSitesData = [];

        if (response.data?.data && Array.isArray(response.data.data)) {
          // Standard structure: response.data.data is an array
          chargingSitesData = response.data.data;
        } else if (
          response.data?.data &&
          typeof response.data.data === "object"
        ) {
          // response.data.data is an object, check if it has a results/items property
          if (
            response.data.data.results &&
            Array.isArray(response.data.data.results)
          ) {
            chargingSitesData = response.data.data.results;
          } else if (
            response.data.data.items &&
            Array.isArray(response.data.data.items)
          ) {
            chargingSitesData = response.data.data.items;
          } else if (
            response.data.data.chargingSites &&
            Array.isArray(response.data.data.chargingSites)
          ) {
            chargingSitesData = response.data.data.chargingSites;
          } else if (
            response.data.data.chargeZones &&
            Array.isArray(response.data.data.chargeZones)
          ) {
            chargingSitesData = response.data.data.chargeZones;
          }
        } else if (Array.isArray(response.data)) {
          // Direct array response
          chargingSitesData = response.data;
        } else if (
          response.data?.success &&
          response.data?.data &&
          Array.isArray(response.data.data)
        ) {
          // Success wrapper with data array
          chargingSitesData = response.data.data;
        } else {
          // Fallback: try to find any array in the response
          if (response.data?.results && Array.isArray(response.data.results)) {
            chargingSitesData = response.data.results;
          } else if (
            response.data?.items &&
            Array.isArray(response.data.items)
          ) {
            chargingSitesData = response.data.items;
          } else if (
            response.data?.chargingSites &&
            Array.isArray(response.data.chargingSites)
          ) {
            chargingSitesData = response.data.chargingSites;
          } else if (
            response.data?.chargeZones &&
            Array.isArray(response.data.chargeZones)
          ) {
            chargingSitesData = response.data.chargeZones;
          } else {
            chargingSitesData = [];
          }
        }

        // Map the API response to match table column expectations
        if (chargingSitesData.length > 0) {
          const mappedChargingSites = chargingSitesData.map((site) => ({
            zone_id: site.id || site.zone_id,
            zone_name: site.zone_name || site.name || site.zoneName,
            load_balancing_value:
              site.load_balancing_value || site.loadBalancingValue || "N/A",
            load_balancing_unit:
              site.load_balancing_unit || site.loadBalancingUnit || "N/A",
            isLoadBalancingReq: Number(site.is_load_balancing || 0),
            chargers: site.chargers || [],
          }));

          setChargerList(mappedChargingSites);
          setTableData(mappedChargingSites);
        } else {
          setChargerList([]);
          setTableData([]);
        }
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      errorNotification(err);
      setChargerList([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial data fetch when component mounts or teamId changes
  useEffect(() => {
    if (teamId && !initialDataFetchedRef.current) {
      getChargerList();
      fetchAvailableChargers();
      initialDataFetchedRef.current = true;
    }

    // Reset form submission state on initial mount
    setIsFormSubmitting(false);
  }, [teamId]);

  // Refetch data when mode changes (NEW vs EDIT/LIST)
  useEffect(() => {
    if (teamId && initialDataFetchedRef.current) {
      getChargerList();
    }
  }, [isAddMode, isEditMode, teamId]);

  // Monitor form data changes
  useEffect(() => {}, [formData]);

  // Handle route-based initialization
  useEffect(() => {
    // Reset the ref when mode changes
    if (isAddMode !== isEditMode) {
      hasFetchedChargingSiteRef.current = false;
    }

    if (isAddMode) {
      setCreateZoneBtn(true);
      setFormData({
        zoneName: "",
        isLoadBalancingReq: 0,
        loadBalancingUnit: "kW",
        loadBalancingValue: "",
        chargers: [],
      });
      setSelectedChargerId("");

      // Refresh available chargers for add mode
      if (teamId) {
        const refreshUnassignedChargers = async () => {
          try {
            const response = await axios.get(getTeamChargerIds(teamId, true));

            // Extract charger IDs from the response - try different possible structures
            let chargerIds = [];

            if (
              response.data?.chargerIds &&
              Array.isArray(response.data.chargerIds)
            ) {
              chargerIds = response.data.chargerIds;
            } else if (
              response.data?.data?.chargerIds &&
              Array.isArray(response.data.data.chargerIds)
            ) {
              chargerIds = response.data.data.chargerIds;
            } else if (Array.isArray(response.data)) {
              chargerIds = response.data;
            } else if (
              response.data?.data &&
              Array.isArray(response.data.data)
            ) {
              chargerIds = response.data.data;
            }

            // Always clear the available chargers first, then set new data
            setAvailableChargers([]);

            // Only set new data if we have charger IDs
            if (chargerIds && chargerIds.length > 0) {
              // Convert charger IDs to objects for the dropdown
              const allChargers = chargerIds.map((id) => ({
                chargepoint_id: id,
                name: id,
              }));

              setAvailableChargers(allChargers);
            }
          } catch (err) {
            console.error("Error calling getTeamChargerIds for add mode:", err);
            setAvailableChargers([]);
          }
        };

        refreshUnassignedChargers();
      }
    } else if (isEditMode && teamId && id) {
      // Set the update mode state
      setUpdateChargingSite({ value: true, zoneId: id });

      // Only fetch if we haven't fetched yet or if the ID changed
      if (
        !hasFetchedChargingSiteRef.current ||
        hasFetchedChargingSiteRef.current !== id
      ) {
        hasFetchedChargingSiteRef.current = id;

        // Call getTeamChargerIds before loading edit page to refresh unassigned chargers
        const refreshUnassignedChargers = async () => {
          try {
            const response = await axios.get(getTeamChargerIds(teamId, true));

            // Extract charger IDs from the response - try different possible structures
            let chargerIds = [];

            if (
              response.data?.chargerIds &&
              Array.isArray(response.data.chargerIds)
            ) {
              chargerIds = response.data.chargerIds;
            } else if (
              response.data?.data?.chargerIds &&
              Array.isArray(response.data.data.chargerIds)
            ) {
              chargerIds = response.data.data.chargerIds;
            } else if (Array.isArray(response.data)) {
              chargerIds = response.data;
            } else if (
              response.data?.data &&
              Array.isArray(response.data.data)
            ) {
              chargerIds = response.data.data;
            }

            // Always clear the available chargers first, then set new data
            setAvailableChargers([]);

            // Only set new data if we have charger IDs
            if (chargerIds && chargerIds.length > 0) {
              // Convert charger IDs to objects for the dropdown
              const allChargers = chargerIds.map((id) => ({
                chargepoint_id: id,
                name: id,
              }));

              setAvailableChargers(allChargers);
            }
          } catch (err) {
            console.error(
              "Error calling getTeamChargerIds before edit page load:",
              err
            );
            setAvailableChargers([]);
          }
        };

        refreshUnassignedChargers();
        fetchChargingSiteById(id);
      }
    }

    // Reset form submission state when route changes
    setIsFormSubmitting(false);
  }, [isAddMode, isEditMode, id, teamId]);

  // Clear available chargers when teamId changes
  useEffect(() => {
    setAvailableChargers([]);
  }, [teamId]);

  const handleAddCharger = () => {
    if (selectedChargerId) {
      const chargerToAdd = availableChargers.find(
        (c) => c.chargepoint_id === selectedChargerId
      );

      if (chargerToAdd) {
        // Check for duplicates using multiple possible ID properties
        const isDuplicate = formData.chargers.some((charger) => {
          const existingId =
            charger.chargepoint_id || charger.id || charger.chargerId;
          const newId =
            chargerToAdd.chargepoint_id ||
            chargerToAdd.id ||
            chargerToAdd.chargerId;
          return existingId === newId;
        });

        if (!isDuplicate) {
          setFormData((prev) => ({
            ...prev,
            chargers: [...prev.chargers, chargerToAdd],
          }));
          setSelectedChargerId("");
        } else {
          // Show error message for duplicate
          dispatch(
            setAlert({
              open: true,
              message: "This charger is already added to the charging site",
              severity: enums.SEVERITY.WARNING,
            })
          );
        }
      }
    }
  };

  const handleRemoveCharger = (index) => {
    // Get the charger being removed before filtering
    const removedCharger = formData.chargers[index];

    setFormData((prev) => ({
      ...prev,
      chargers: prev.chargers.filter((_, i) => i !== index),
    }));

    // Immediately add the removed charger back to available chargers list
    if (removedCharger) {
      setAvailableChargers((prev) => {
        // Check if charger is not already in the list to avoid duplicates
        const isAlreadyAvailable = prev.some((charger) => {
          const existingId =
            charger.chargepoint_id || charger.id || charger.chargerId;
          const removedId =
            removedCharger.chargepoint_id ||
            removedCharger.id ||
            removedCharger.chargerId;
          return existingId === removedId;
        });

        if (!isAlreadyAvailable) {
          return [...prev, removedCharger];
        }
        return prev;
      });
    }
  };

  const handleCreateZone = () => {
    setCreateZoneBtn(true);
    setFormData({
      zoneName: "",
      isLoadBalancingReq: 0,
      loadBalancingUnit: "kW",
      loadBalancingValue: "",
      chargers: [],
    });
    setSelectedChargerId("");
    navigate("/charging-site/new");
  };

  const handleCancel = () => {
    setCreateZoneBtn(false);
    setUpdateChargingSite({ value: false, zoneId: "" });
    setFormData({
      zoneName: "",
      isLoadBalancingReq: 0,
      loadBalancingUnit: "kW",
      loadBalancingValue: "",
      chargers: [],
    });
    setSelectedChargerId("");
    navigate("/charging-site");
  };

  // If teamId is not available, show loading or return early
  if (!teamId) {
    return (
      <Page>
        <Card sx={{ p: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Typography>Loading team information...</Typography>
          </Box>
        </Card>
      </Page>
    );
  }

  // If we're in add/edit mode and the form is open, show only the main component
  const shouldShowForm =
    (createZoneBtn || isAddMode || isEditMode) && (isAddMode || isEditMode);

  if (shouldShowForm) {
    return (
      <Page>
        <Card sx={{ p: 4 }}>
          <form onSubmit={handleSubmitForm}>
            <Box
              sx={{
                minHeight: "500px",
                minWidth: "400px",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <div>
                <Typography sx={{ mb: 2, fontWeight: "600", fontSize: "16px" }}>
                  {isReadOnly
                    ? "View Charging Site"
                    : isEditMode
                    ? "Edit Charging Site"
                    : "Create Charging Site"}
                </Typography>
              </div>
              <Box>
                <TextField
                  sx={{ width: "100%" }}
                  id="zone-name-field"
                  label="Zone Name *"
                  variant="outlined"
                  name="zoneName"
                  value={formData.zoneName}
                  onChange={isReadOnly ? undefined : handleChange}
                  required={!isReadOnly}
                  inputProps={{
                    id: "zone-name-input",
                    "aria-label": "Zone Name (required)",
                    readOnly: isReadOnly,
                  }}
                  InputLabelProps={{
                    htmlFor: "zone-name-input",
                  }}
                  InputProps={{
                    readOnly: isReadOnly,
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", gap: "1rem", placeItems: "center" }}>
                <Typography id="load-balance-label">
                  Load Balancing Required:{" "}
                </Typography>
                <RadioGroup
                  aria-labelledby="load-balance-label"
                  name="isLoadBalancingReq"
                  sx={{ flexDirection: "row" }}
                  value={formData.isLoadBalancingReq}
                  onChange={isReadOnly ? undefined : handleChange}
                >
                  <FormControlLabel
                    value={1}
                    control={<Radio />}
                    label="Yes"
                    disabled={isReadOnly}
                  />
                  <FormControlLabel
                    value={0}
                    control={<Radio />}
                    label="No"
                    disabled={isReadOnly}
                  />
                </RadioGroup>
              </Box>

              <Box>
                <TextField
                  sx={{ width: "100%" }}
                  id="load-limit-field"
                  label="Load Value *"
                  variant="outlined"
                  name="loadBalancingValue"
                  type="number"
                  value={formData.loadBalancingValue}
                  onChange={isReadOnly ? undefined : handleChange}
                  required={!isReadOnly}
                  placeholder="Enter load value"
                  inputProps={{
                    id: "load-limit-input",
                    "aria-label": "Load Limit (required)",
                  }}
                  InputLabelProps={{
                    htmlFor: "load-limit-input",
                  }}
                  InputProps={{
                    readOnly: isReadOnly,
                    endAdornment: (
                      <InputAdornment position="end">
                        <NativeSelect
                          inputProps={{
                            name: "loadBalancingUnit",
                            id: "load-balancing-unit-select",
                            "aria-label": "Load Balancing Unit",
                            disabled: isReadOnly,
                          }}
                          value={formData.loadBalancingUnit}
                          onChange={isReadOnly ? undefined : handleChange}
                          sx={{ width: "100px" }}
                          aria-label="Load Balancing Unit"
                        >
                          <option value="kW">kW</option>
                          <option value="kWh">kWh</option>
                          <option value="A">A</option>
                        </NativeSelect>
                      </InputAdornment>
                    ),
                  }}
                />
                {!isReadOnly && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Load unit must be kW, kWh, or A. Load value must be a
                    non-negative number.
                  </Typography>
                )}
              </Box>

              {/* Charger Management Section */}
              <Box
                sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 2 }}
              >
                <Typography sx={{ mb: 2, fontWeight: "600", fontSize: "14px" }}>
                  Chargers in this Site
                </Typography>

                {/* Display attached chargers */}
                {formData.chargers && formData.chargers.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, color: "text.secondary" }}
                    >
                      Attached Chargers ({formData.chargers.length}):
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {formData.chargers.map((charger, index) => (
                        <Chip
                          key={index}
                          label={
                            charger.chargepoint_id ||
                            charger.id ||
                            charger.chargerId ||
                            charger.name ||
                            `Charger ${index + 1}`
                          }
                          onDelete={
                            isReadOnly
                              ? undefined
                              : () => handleRemoveCharger(index)
                          }
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ mb: 2, color: "text.secondary" }}
                  >
                    No chargers attached to this site.
                  </Typography>
                )}

                {/* Add existing chargers */}
                {!isReadOnly && (
                  <>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        id="add-existing-charger-select"
                        select
                        label="Add Existing Charger"
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: "250px" }}
                        value={selectedChargerId || ""}
                        onChange={(e) => setSelectedChargerId(e.target.value)}
                        inputProps={{
                          id: "add-existing-charger-input",
                          "aria-label": "Add Existing Charger",
                        }}
                        InputLabelProps={{
                          htmlFor: "add-existing-charger-input",
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a charger to add</em>
                        </MenuItem>
                        {availableChargers &&
                        Array.isArray(availableChargers) &&
                        availableChargers.length > 0 ? (
                          availableChargers
                            .filter((charger) => {
                              // Filter out chargers that are already added
                              const chargerId =
                                charger.chargepoint_id ||
                                charger.id ||
                                charger.chargerId;
                              return !formData.chargers.some(
                                (existingCharger) => {
                                  const existingId =
                                    existingCharger.chargepoint_id ||
                                    existingCharger.id ||
                                    existingCharger.chargerId;
                                  return existingId === chargerId;
                                }
                              );
                            })
                            .map((charger, index) => {
                              const chargerId =
                                charger.chargepoint_id ||
                                charger.id ||
                                charger.chargerId;
                              return (
                                <MenuItem
                                  key={`${chargerId}-${index}`}
                                  value={chargerId}
                                >
                                  {chargerId}
                                </MenuItem>
                              );
                            })
                        ) : (
                          <MenuItem key="no-chargers" disabled>
                            No chargers found
                          </MenuItem>
                        )}
                      </TextField>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAddCharger}
                        disabled={!selectedChargerId}
                      >
                        Add Charger
                      </Button>
                    </Box>

                    {/* Show unassigned chargers count */}
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary", fontSize: "12px" }}
                    >
                      {(() => {
                        const availableCount =
                          availableChargers?.filter((charger) => {
                            const chargerId =
                              charger.chargepoint_id ||
                              charger.id ||
                              charger.chargerId;
                            return !formData.chargers.some(
                              (existingCharger) => {
                                const existingId =
                                  existingCharger.chargepoint_id ||
                                  existingCharger.id ||
                                  existingCharger.chargerId;
                                return existingId === chargerId;
                              }
                            );
                          }).length || 0;

                        return availableCount > 0
                          ? `${availableCount} unassigned charger(s) available`
                          : "No unassigned chargers found";
                      })()}
                    </Typography>
                  </>
                )}
              </Box>

              {!isReadOnly && (
                <Box sx={{ display: "flex", placeContent: "end", gap: "1rem" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isFormSubmitting}
                    startIcon={
                      isFormSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : null
                    }
                  >
                    {isFormSubmitting
                      ? "Saving..."
                      : isEditMode
                      ? "Update"
                      : "Create"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isFormSubmitting}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
              {isReadOnly && (
                <Box sx={{ display: "flex", placeContent: "end" }}>
                  <Button variant="outlined" onClick={handleCancel}>
                    Close
                  </Button>
                </Box>
              )}
            </Box>
          </form>
        </Card>
      </Page>
    );
  }

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box>
      <Stack direction="row" spacing={3} mb={4}>
        {[1, 2].map((item) => (
          <Skeleton
            key={item}
            variant="rectangular"
            width="100%"
            height={120}
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Stack>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={400}
        sx={{ borderRadius: 2 }}
      />
    </Box>
  );

  // Empty state component
  const EmptyState = () => (
    <Fade in={true}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 8,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "50%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Icon icon={homeFill} width={60} height={60} color="#9e9e9e" />
        </Box>
        <Typography variant="h5" gutterBottom color="text.secondary">
          {searchValue ? "No charging sites found" : "No charging sites yet"}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 400 }}
        >
          {searchValue
            ? `No charging sites match your search "${searchValue}". Try adjusting your search terms.`
            : "Get started by creating your first charging site to organize and manage your charging infrastructure."}
        </Typography>
        {hasCreateAccess && !searchValue && isTeamSelected && (
          <Button
            variant="contained"
            onClick={handleCreateZone}
            startIcon={<Icon icon={plusFill} width={20} height={20} />}
            sx={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              boxShadow: "0 4px 14px 0 rgba(25, 118, 210, 0.39)",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                boxShadow: "0 6px 20px 0 rgba(25, 118, 210, 0.5)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Create Charging Site
          </Button>
        )}
      </Box>
    </Fade>
  );

  return (
    <Page
      title="Charging Sites"
      {...(hasCreateAccess &&
        !isAddMode &&
        isTeamSelected && { action: handleCreateZone })}
      actionButtonText="Create Charging Site"
    >
      {/* Page Header */}
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box>
            {/* <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
                            Charging Sites
                        </Typography> */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              Manage charging sites, load limit and load balancing
              configurations
            </Typography>
          </Box>
        </Stack>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <StyledListCard>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
                  >
                    {isLoading ? <Skeleton width={40} /> : totalSites}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Charging Sites
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon icon={homeFill} width={32} height={32} color="white" />
                </Box>
              </Stack>
            </StyledListCard>
          </Grid>

          <Grid item xs={12} sm={6}>
            <StyledListCard>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
                  >
                    {isLoading ? (
                      <Skeleton width={40} />
                    ) : (
                      sitesWithLoadBalancing
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Load Balanced Sites
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    bgcolor: "success.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon
                    icon={settingsFill}
                    width={32}
                    height={32}
                    color="white"
                  />
                </Box>
              </Stack>
            </StyledListCard>
          </Grid>
        </Grid>
      </Box>

      {/* Search and Table in Single Card */}
      <StyledListCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search charging sites..."
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isLoading}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "&:disabled": {
                    bgcolor: "grey.300",
                    color: "grey.500",
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !tableData || tableData.length === 0 ? (
          <EmptyState />
        ) : (
          <BaseTable
            rows={tableData}
            columns={chargingSiteColumns}
            loading={isLoading}
            getRowId={(row) => row.zone_id || row.id}
            pageSize={10}
            isReadOnly={isReadOnly}
            showExportCsvBtn={false}
            rowOrCellClickRedirect={rowOrCellClickRedirect}
            height={Math.max(200, (tableData?.length || 0) * 52 + 100)}
            sx={{
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-main": {
                height: "auto !important",
                minHeight: "auto !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                height: "auto !important",
                minHeight: "auto !important",
              },
              "& .MuiDataGrid-row": {
                height: "52px !important",
                cursor: isReadOnly ? "default" : "pointer",
                "&:hover": {
                  backgroundColor: isReadOnly ? "transparent" : "action.hover",
                },
              },
              "& .MuiDataGrid-cell": {
                height: "52px !important",
                cursor: isReadOnly ? "default" : "pointer",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: (theme) => theme.palette.primary.lighter,
                borderBottom: (theme) =>
                  `2px solid ${theme.palette.primary.main}`,
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: 600,
                color: (theme) => theme.palette.primary.main,
              },
              "& .MuiDataGrid-cell": {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: (theme) => theme.palette.action.hover,
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
                borderBottom: isReadOnly ? "none" : "1px solid",
                borderBottomColor: "primary.light",
              },
            }}
          />
        )}
      </StyledListCard>
    </Page>
  );
};

export default ChargingSite;
